/**
 * [Insider+] Header support dropdown shown only for Insider+ subscribers.
 */

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { ReactComponent as DropdownIcon } from '../../assets/images/svg/dropdown.svg';
import { buttonizeDiv } from '../../util/buttonizeDiv';
import themeColors from '../../assets/styles/themeColors';
import { variables } from '../../assets/styles/variables';
import { mediaBelow, mediaUp, pxToRem } from '../../assets/styles/helper';
import {
  uFlexCenter,
  uResetPosition,
  uTransition,
} from '../../assets/styles/utility';

const DropdownList = styled.ul`
  position: absolute;
  top: 100%;
  left: 50%;
  right: 0;
  width: max-content;
  min-width: 13rem;
  background-color: ${themeColors.colorNavigationDropdownBackground};
  z-index: ${variables.zIndex.indexSm};
  display: none;
  transform: translateX(-50%);
  box-shadow: ${variables.shadow.boxShadow};

  ${mediaBelow(variables.breakpoints.bpXl)} {
    ${uResetPosition};
    width: 100%;
    transform: translateX(0);
    background-color: ${themeColors.colorSecondaryHeaderBackground};
  }
`;

const DropdownIconWrapper = styled.div`
  margin-left: ${pxToRem(8)};

  svg {
    ${uTransition};
    width: 1rem;
    height: 1rem;
    color: ${themeColors.colorTextContrast};
  }

  ${mediaBelow(variables.breakpoints.bpXl)} {
    margin-left: auto;

    svg {
      color: ${themeColors.colorTextPrimary};
      width: ${pxToRem(18)};
      height: ${pxToRem(18)};
    }
  }
`;

const DropdownContainer = styled.div`
  ${uFlexCenter};
  position: relative;
  height: 100%;
  width: 100%;
  cursor: pointer;

  ${mediaBelow(variables.breakpoints.bpXl)} {
    flex-direction: column;

    ${({ isMobile }) =>
      isMobile &&
      `display: flex;

      ${DropdownList} {
        display: none;
      }
    `}

    ${({ isMobileOpen }) =>
      isMobileOpen &&
      `
      ${DropdownIconWrapper} {
        svg {
          transform: rotate(-180deg);
        }
      }

      ${DropdownList} {
        display: block;
      }
    `}
  }

  ${mediaUp(variables.breakpoints.bpXl)} {
    &:hover {
      ${DropdownIconWrapper} {
        svg {
          transform: rotate(-180deg);
        }
      }

      ${DropdownList} {
        display: block;
      }
    }
  }
`;

const Label = styled.span`
  ${uFlexCenter};
  text-decoration: none;
  line-height: 1.5;
  font-size: ${pxToRem(18)};
  color: ${themeColors.colorTextContrast};
  text-transform: uppercase;
  height: 100%;
  width: 100%;

  ${mediaBelow(variables.breakpoints.bpXl)} {
    color: ${themeColors.colorTextPrimary};
    justify-content: flex-start;
    align-items: center;
    padding: ${pxToRem(14)} ${pxToRem(18)};
    font-size: ${pxToRem(20)};
    line-height: 1.35;
    text-transform: capitalize;

    &:hover {
      background-color: ${themeColors.colorNavigationItemHover};
    }
  }
`;

const PhoneIconWrapper = styled.span`
  margin-right: ${pxToRem(6)};
  display: flex;
  align-items: center;

  svg {
    color: ${themeColors.colorTextContrast};
  }
`;

const EmailIconWrapper = styled.span`
  margin-right: ${pxToRem(8)};
  display: flex;
  align-items: center;
  flex-shrink: 0;

  svg {
    color: ${themeColors.colorTextHeader};
  }
`;

const DropdownItem = styled.li`
  display: flex;
  flex-shrink: 0;

  &:hover {
    background-color: ${themeColors.colorNavigationDropdownItemHoverBackground};
  }

  > a {
    display: flex;
    align-items: center;
    font-size: ${pxToRem(16)};
    line-height: 1.75;
    color: ${themeColors.colorTextHeader};
    padding: 4px ${pxToRem(16)};
    width: 100%;
    text-decoration: none;

    white-space: nowrap;

    &:hover {
      font-weight: 600;
    }
  }

  ${mediaBelow(variables.breakpoints.bpXl)} {
    display: flex;

    &:hover {
      background-color: ${themeColors.colorNavigationItemHover};
    }

    > a {
      color: ${themeColors.colorTextPrimary};
      font-size: ${pxToRem(21)};
      line-height: 1.35;
      padding: ${pxToRem(10)} ${pxToRem(36)};
    }
  }
`;

const PhoneIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M15.05 5A5 5 0 0119 8.95M15.05 1A9 9 0 0123 8.94M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.8a19.79 19.79 0 01-3.07-8.7A2 2 0 012 .82h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
  </svg>
);

const EmailIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const formatPhone = (phone) => {
  if (!phone) return phone;
  const digits = phone.replace(/\D/g, '');
  // Handle 11-digit numbers with country code (e.g. 18874178945)
  const local = digits.length === 11 ? digits.slice(1) : digits;
  if (local.length !== 10) return phone;
  return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
};

const InsiderPlusSupportDropdown = ({ phone, email, isNavOpen, isMobile }) => {
  const [isOpen, setIsOpen] = useState(isMobile);

  useEffect(() => {
    if (isNavOpen) {
      setIsOpen(false);
    }
  }, [isNavOpen, isMobile]);

  if (!phone && !email) {
    return null;
  }

  return (
    <DropdownContainer
      isMobile={isMobile}
      isMobileOpen={!isOpen && isMobile && isNavOpen}
      {...buttonizeDiv(() => setIsOpen(!isOpen))}
      data-cy="nav-insider-support"
    >
      <Label>
        <PhoneIconWrapper>
          <PhoneIcon />
        </PhoneIconWrapper>
        {formatPhone(phone)}
        <DropdownIconWrapper>
          <DropdownIcon />
        </DropdownIconWrapper>
      </Label>
      <DropdownList>
        {email && (
          <DropdownItem>
            <a href={`mailto:${email}`}>
              <EmailIconWrapper>
                <EmailIcon />
              </EmailIconWrapper>
              {email}
            </a>
          </DropdownItem>
        )}
      </DropdownList>
    </DropdownContainer>
  );
};

InsiderPlusSupportDropdown.propTypes = {
  phone: PropTypes.string,
  email: PropTypes.string,
  isNavOpen: PropTypes.bool,
  isMobile: PropTypes.bool,
};

export default InsiderPlusSupportDropdown;
